import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usePromoCodeUsageList } from './hooks'
import { ColumnsType } from 'antd/lib/table'
import { CCardBody } from '@coreui/react-pro'
import { PromoCodeUsage } from './types'
import { Button, DatePicker, Flex, Input, Table, Tag, Typography } from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import Highlighter from 'react-highlight-words'
import { useUrlFilters } from '../../../../libs/useUrlFilters'
import { path } from '../../../../libs'

const { Text } = Typography
const { RangePicker } = DatePicker

// ── Filter config ──────────────────────────────────────────
const FILTER_CONFIG = {
  code: { type: 'string' as const },
  userId: { type: 'string' as const },
  activatedFrom: { type: 'string' as const },
  activatedTo: { type: 'string' as const },
  depositedFrom: { type: 'string' as const },
  depositedTo: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'id', pagination: true },
  sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
}

export const PromoCodeUsageList: React.FC = () => {
  const {
    promoCodeUsageList,
    onChangePromoCodeUsageList,
    totalCountOfPromoCodeUsageList,
    loading,
  } = usePromoCodeUsageList()

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'id'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'

  // Local input state — applied to URL only on Enter/blur
  const [inputCode, setInputCode] = useState(filters.code || '')
  const [inputUserId, setInputUserId] = useState(filters.userId || '')

  // ── Build API searchText from URL state ─────────────────
  const buildSearchText = useCallback(() => {
    const s: Record<string, any> = {}
    if (filters.code) s.code = filters.code
    if (filters.userId) s.userId = filters.userId
    if (filters.activatedFrom || filters.activatedTo) {
      s.activatedAt = [
        filters.activatedFrom ? dayjs(filters.activatedFrom) : null,
        filters.activatedTo ? dayjs(filters.activatedTo) : null,
      ]
    }
    if (filters.depositedFrom || filters.depositedTo) {
      s.depositedAt = [
        filters.depositedFrom ? dayjs(filters.depositedFrom) : null,
        filters.depositedTo ? dayjs(filters.depositedTo) : null,
      ]
    }
    return s
  }, [filters])

  // ── Fetch data when filters change ──────────────────────
  useEffect(() => {
    onChangePromoCodeUsageList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText: buildSearchText(),
    })
  }, [filters, page, pageSize, orderBy, orderDirection])

  // ── Text search (applied on Enter/blur) ─────────────────
  const applySearch = (key: 'code' | 'userId', value: string) => {
    setFilters({ [key]: value.trim() || undefined, page: undefined } as any)
  }

  const clearSearch = (
    key: 'code' | 'userId',
    setter: (v: string) => void,
  ) => {
    setter('')
    setFilters({ [key]: undefined, page: undefined } as any)
  }

  // ── Date ranges from URL ────────────────────────────────
  const activatedRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null =
    filters.activatedFrom || filters.activatedTo
      ? [
          filters.activatedFrom ? dayjs(filters.activatedFrom) : null,
          filters.activatedTo ? dayjs(filters.activatedTo) : null,
        ]
      : null

  const depositedRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null =
    filters.depositedFrom || filters.depositedTo
      ? [
          filters.depositedFrom ? dayjs(filters.depositedFrom) : null,
          filters.depositedTo ? dayjs(filters.depositedTo) : null,
        ]
      : null

  // ── Active filter tags ──────────────────────────────────
  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.code)
      tags.push({
        key: 'code',
        label: `Promo-code: ${filters.code}`,
        clear: () => clearSearch('code', setInputCode),
      })
    if (filters.userId)
      tags.push({
        key: 'userId',
        label: `Client ID: ${filters.userId}`,
        clear: () => clearSearch('userId', setInputUserId),
      })
    if (filters.activatedFrom || filters.activatedTo)
      tags.push({
        key: 'activated',
        label: `Activated: ${filters.activatedFrom || '…'} — ${
          filters.activatedTo || '…'
        }`,
        clear: () =>
          setFilters({
            activatedFrom: undefined,
            activatedTo: undefined,
            page: undefined,
          } as any),
      })
    if (filters.depositedFrom || filters.depositedTo)
      tags.push({
        key: 'deposited',
        label: `Deposited: ${filters.depositedFrom || '…'} — ${
          filters.depositedTo || '…'
        }`,
        clear: () =>
          setFilters({
            depositedFrom: undefined,
            depositedTo: undefined,
            page: undefined,
          } as any),
      })
    return tags
  }, [filters])

  // ── Columns ─────────────────────────────────────────────
  const columns: ColumnsType<PromoCodeUsage> = [
    {
      title: 'Promo-code',
      dataIndex: 'code',
      render: (v: string) => (
        <Highlighter
          searchWords={[inputCode.trim()]}
          highlightStyle={{
            backgroundColor: 'var(--color-yellow-50)',
            padding: 0,
          }}
          autoEscape
          textToHighlight={v ?? ''}
        />
      ),
    },
    {
      title: 'Client ID',
      dataIndex: 'userId',
      render: (id: number) => (
        <Link
          to={path.clients.details(String(id))}
          style={{ textDecoration: 'none' }}
        >
          <Highlighter
            searchWords={[inputUserId.trim()]}
            highlightStyle={{
              backgroundColor: 'var(--color-yellow-50)',
              padding: 0,
            }}
            autoEscape
            textToHighlight={String(id ?? '')}
          />
        </Link>
      ),
    },
    {
      title: 'Activated At',
      dataIndex: 'activatedAt',
      sorter: true,
    },
    {
      title: 'Bonus %',
      dataIndex: 'bonusPercent',
      sorter: true,
    },
    {
      title: 'Deposited At',
      dataIndex: 'depositedAt',
      sorter: true,
    },
    {
      title: 'Deposited',
      dataIndex: 'depositUsd',
      sorter: true,
      render: (depositUsd: number) => (depositUsd ? `${depositUsd} USD` : '-'),
    },
  ]

  return (
    <CCardBody>
      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        {/* Row 1: text search fields */}
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Promo-code"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            onPressEnter={() => applySearch('code', inputCode)}
            onBlur={() => applySearch('code', inputCode)}
            allowClear
            onClear={() => clearSearch('code', setInputCode)}
            style={{ width: 200 }}
          />
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Client ID"
            value={inputUserId}
            onChange={(e) =>
              setInputUserId(e.target.value.replace(/\D/g, ''))
            }
            onPressEnter={() => applySearch('userId', inputUserId)}
            onBlur={() => applySearch('userId', inputUserId)}
            allowClear
            onClear={() => clearSearch('userId', setInputUserId)}
            style={{ width: 140 }}
          />
        </Flex>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: 'var(--crm-border, #e2e5ec)',
            margin: '0 0 10px',
          }}
        />

        {/* Row 2: date range filters */}
        <Flex gap={10} wrap="wrap" align="center">
          <RangePicker
            value={activatedRange}
            allowEmpty={[true, true]}
            onChange={(vals) =>
              setFilters({
                activatedFrom: vals?.[0]
                  ? vals[0].format('YYYY-MM-DD')
                  : undefined,
                activatedTo: vals?.[1]
                  ? vals[1].format('YYYY-MM-DD')
                  : undefined,
                page: undefined,
              } as any)
            }
            format="YYYY-MM-DD"
            placeholder={['Activated from', 'to']}
            style={{ width: 260 }}
          />
          <RangePicker
            value={depositedRange}
            allowEmpty={[true, true]}
            onChange={(vals) =>
              setFilters({
                depositedFrom: vals?.[0]
                  ? vals[0].format('YYYY-MM-DD')
                  : undefined,
                depositedTo: vals?.[1]
                  ? vals[1].format('YYYY-MM-DD')
                  : undefined,
                page: undefined,
              } as any)
            }
            format="YYYY-MM-DD"
            placeholder={['Deposited from', 'to']}
            style={{ width: 260 }}
          />
        </Flex>

        {/* Row 3: active filter tags */}
        {activeTags.length > 0 && (
          <>
            <div
              style={{
                height: 1,
                background: 'var(--crm-border, #e2e5ec)',
                margin: '10px 0 8px',
              }}
            />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((t) => (
                <Tag
                  key={t.key}
                  closable
                  onClose={t.clear}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {t.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputCode('')
                  setInputUserId('')
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────── */}
      <Table
        dataSource={promoCodeUsageList}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="promoCodeUsageID"
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfPromoCodeUsageList,
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current)
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          if (sorter.order) {
            // @ts-ignore
            updates.sortField = sorter.field === 'id' ? undefined : sorter.field
            // @ts-ignore
            updates.sortOrder = sorter.order === 'descend' ? undefined : 'ascend'
          } else {
            updates.sortField = undefined
            updates.sortOrder = undefined
          }
          setFilters(updates as any)
        }}
      />
    </CCardBody>
  )
}
